import * as React from "react";
import { Box, Typography, useTheme, Grid, InputBase} from "@mui/material";
import TableSearchRealtime from "../../components/TableSearchRealtime";
import {useEffect, useState, useRef} from 'react';
import axios from 'axios';
import {API_ENDPOINT_URL} from "../../util/api";
import { getLocalItem } from "../../util/helper";
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import _ from 'lodash'; 

const timezone = 'Asia/Tokyo';

const columns = [
    {
      field: "is_phish",
      headerName: "isPhish",
      flex: 1
    },
    {
      field: "domain",
      headerName: "Domain",
      flex: 1,
      cellClassName: "name-column--cell"
    },
    {
      field: "url",
      headerName: "URL",
      flex: 1
    },
    {
      field: "brand",
      headerName: "Brand",
      flex: 1
    },
    {
      field: "score",
      headerName: "DomainScore",
      flex: 1
    },
    {
      field: "is_alive",
      headerName: "SiteAlive",
      flex: 1
    },
    {
      field: "payload_created",
      headerName: "Created",
      flex: 1
    },
  ];

// Set options for formatting date and time
const japanTimeOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZone: 'Asia/Tokyo', // Japan timezone
};

const RealtimeReport = () => {
  const [selectedTime, setSelectedTime] = useState(30);
  const [loading, setLoading]= useState(false)
  const token = getLocalItem("key")
  const [searchStartDate, setSearchStartDate] = useState(null);
  const [searchEndDate, setSearchEndDate] = useState(null);
  const [searchParam, setSearchParam] = useState({ordering: 'payload_created,domain',});
  const [currentPage, setCurrentPage] = useState(1);

  const [data, setData] = useState([]);
  const originDataRef = useRef([]);

  const handleSearch = (searchParamsVar) => {
    const { searchText, currentPageChange, sort } = searchParamsVar;
    setCurrentPage(currentPageChange)
    setSearchParam({
      begin_date: searchParam.begin_date,
      end_date: searchParam.end_date,
      page: currentPageChange,
      ordering: sort.column !== "domain"
        ? `${sort.order}${sort.column},domain`
        : `${sort.order}${sort.column}`,
      search: searchText,
    });
  };

  // Handle change in the Select component
  const handleSelectChange = (event) => {
    setCurrentPage(1)
    searchParam.page = 1
    setSearchParam(searchParam)
    setSelectedTime(event.target.value);
  };

  useEffect(() => {
    const currentDateTime = new Date();
    const searchStartDate = new Date(currentDateTime.getTime() - selectedTime * 1000);
  
    const japanCurrentDateTime = currentDateTime.toLocaleString('ja-JP', japanTimeOptions);
    const searchStartDateString = searchStartDate.toLocaleString('ja-JP', japanTimeOptions);
  
    setSearchStartDate(searchStartDate);
    setSearchEndDate(currentDateTime);
    const formattedSearchRange = `Searching: ${searchStartDateString} ~ ${japanCurrentDateTime}`;
    setSearchRange(formattedSearchRange);

    searchParam.begin_date = searchStartDateString;
    searchParam.end_date = japanCurrentDateTime;
  }, [selectedTime]);

  const [searchRange, setSearchRange] = useState('');

  useEffect(() => {
    setLoading(true)
    axios.get(`${API_ENDPOINT_URL}/data/realtime/`, { params: { ...searchParam }, headers: { Authorization: `Bearer ${token}` } })
        .then(response => {
          setData(_.cloneDeep(response.data));
          originDataRef.current = _.cloneDeep(response.data)
        })
        .catch(error => {})
        .finally(() => setLoading(false));
  }, [searchParam, selectedTime, currentPage]);

  useEffect(() => {
    if(data?.results?.length > 0) {
      
      if (selectedTime) {
        const intervalId = setInterval(() => {
          axios
            .get(`${API_ENDPOINT_URL}/data/polling/`, {
              params: { ...searchParam },
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
              let temp = _.cloneDeep(originDataRef.current)
              let dataChange = _.cloneDeep(response.data);
              const updatedData = temp.results.map((originItem) => {
                const correspondingItem = dataChange.results.find((item) => item.domain === originItem.domain);
            
                if (correspondingItem && !_.isEqual(originItem, correspondingItem)) {
                  let changedColumns = [];
                  for (let key in originItem) {
                    if (!_.isEqual(originItem[key], correspondingItem[key])) {
                      changedColumns.push(key);
                      originItem[key] = correspondingItem[key];
                    }
                  }
                  originItem.changeColumn = changedColumns;
                } else {
                  originItem.changeColumn = [];
                }
            
                return originItem;
              });
              
              let dataTmp = _.cloneDeep(originDataRef.current);
              dataTmp = {...dataTmp ,results:_.cloneDeep(updatedData)}
              setData(dataTmp);

              let removeKey = _.cloneDeep(updatedData)
              removeKey.map(function(item) { 
                delete item.changeColumn; 
                return item; 
              });
              
              originDataRef.current = {...originDataRef.current, results:removeKey}
            })
            .catch((error) => {});
        }, 2000);
        return () => {
          clearInterval(intervalId); // Clear the interval when the component is unmounted or when data changes
        };
      } else {
        let searchTemp = _.cloneDeep(searchParam)
        
        const intervalId = setInterval(() => {
          const curDate = new Date()
          searchTemp.begin_date = curDate.toLocaleString('ja-JP', japanTimeOptions);
          searchTemp.end_date = curDate.toLocaleString('ja-JP', japanTimeOptions);
          axios
            .get(`${API_ENDPOINT_URL}/data/realtime/`, {
              params: { ...searchTemp },
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
              let dataChange = _.cloneDeep(response.data);
              let updatedResult = dataChange.results.concat(data.results)

              const dataTmp = {...data , results: updatedResult.slice(0, 100)}
              setData(dataTmp);
            })
            .catch((error) => {});
        }, 1000);
        return () => {
          clearInterval(intervalId); // Clear the interval when the component is unmounted or when data changes
        };
      }
    }
  }, [data]);
  
  return (
    <Box
      mt="50px"
      sx={{
        width: "95%",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
        }}
      >
        <Grid container spacing={0}>
        <Grid item lg={6} sm={12} md={6}>
          <Box>
            <Typography
              variant="h1"
              fontSize={40}
              fontWeight="bold"
              color="#2699fb"
              textAlign="left"
            >
              Realtime Process Report
            </Typography>
          </Box>
          <Box mt={4}>
            <Grid container spacing={0} alignItems="center">
              <Grid item>
                <Typography variant="body1" fontSize={20} color="#707070">
                  Search for Created time:
                </Typography>
              </Grid>
              <Grid item>
                
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <Select
                    displayEmpty
                    value={selectedTime}
                    onChange={handleSelectChange}
                    sx={{
                      border: "2px solid #2699fb",
                      borderRadius: "50px",
                      padding: "7px 16px 7px 16px",
                      color: "#2699fb"
                    }}
                  >
                    <MenuItem value={30}>30 seconds ago</MenuItem>
                    <MenuItem value={60}>1 minute ago</MenuItem>
                    <MenuItem value={120}>2 minutes ago</MenuItem>
                    <MenuItem value={180}>3 minutes ago</MenuItem>
                    <MenuItem value={300}>5 minutes ago</MenuItem>
                    <MenuItem value={600}>10 minutes ago</MenuItem>
                    <MenuItem value={0}>now</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Grid>
          <Grid item lg={1} sm={0} md={0}></Grid>
          
        </Grid>
        {data ? (
          <Box mt={5} width="100%">
            <TableSearchRealtime
              searchRange={searchRange}
              rows={data.results}
              columns={columns}
              totalPage={data.last_page_number}
              onSearch={handleSearch}
              loading={loading}
              currentPage={currentPage}
            />
          </Box>
        ) : (
          <p>No data available</p>
        )}
      </Box>
    </Box>
    // </ThemeProvider>
  );
};

export default RealtimeReport;
